export type Politeness = 'polite' | 'assertive';

/**
 * How much the message wants the student's attention — **orthogonal to
 * politeness**, and deliberately not derived from it.
 *
 * Politeness answers *"should this interrupt the screen reader?"*, an ARIA
 * delivery question. Severity answers *"does this need attention?"* The two
 * correlate loosely and diverge exactly where it matters: `announceAssertive`
 * is already used for "Workspace fit to screen." and "Workspace successfully
 * reset." — successes — alongside "Edge of grid. Can't move left." Gating a
 * visible panel on politeness would therefore present confirmations with error
 * weight, and there would be no way to recover the two axes afterwards without
 * auditing every call site.
 */
export type Severity = 'status' | 'success' | 'warning' | 'error';

export interface AnnounceOptions {
  politeness?: Politeness;
  /** Defaults to `status`. NOT inferred from politeness — see `Severity`. */
  severity?: Severity;
}

export type RegionState = Readonly<{ slotA: string; slotB: string }>;

/** The most recent message handed to a live region, whatever its politeness. */
export type LatestAnnouncement = Readonly<{
  text: string;
  politeness: Politeness;
  severity: Severity;
}>;

export type AnnouncerState = Readonly<{
  polite: RegionState;
  assertive: RegionState;
  /**
   * What was said LAST — a single line, politeness irrelevant (STEM-186).
   *
   * The two regions are independent and each holds its own last message, so
   * once both are populated the rendered state no longer says which arrived
   * most recently. `drain` is the only place that knows, because it is the
   * moment a message is handed to a region — so recency is captured here
   * rather than re-derived by a consumer diffing snapshots, which would be a
   * second copy free to disagree with this one.
   *
   * Exists for the persistent announcer panel: a screen reader is transient,
   * and a student who thinks "what was that?" needs somewhere to go and read
   * it again. Purely additive — nothing else reads it, and the live regions
   * are unaffected.
   */
  latest: LatestAnnouncement | null;
}>;

const EMPTY_REGION: RegionState = { slotA: '', slotB: '' };
const EMPTY_STATE: AnnouncerState = {
  polite: EMPTY_REGION,
  assertive: EMPTY_REGION,
  latest: null,
};

const DEFAULT_DRAIN_DELAY_MS = 150;

let state: AnnouncerState = EMPTY_STATE;
/** A queued message keeps its severity so `latest` can carry it through. */
type Queued = Readonly<{ text: string; severity: Severity }>;

const queues: Record<Politeness, Queued[]> = { polite: [], assertive: [] };
const nextSlot: Record<Politeness, 'a' | 'b'> = { polite: 'a', assertive: 'a' };
let drainTimer: ReturnType<typeof setTimeout> | null = null;
let drainDelayMs = DEFAULT_DRAIN_DELAY_MS;

const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function scheduleDrain(): void {
  if (drainTimer !== null) return;
  drainTimer = setTimeout(drain, drainDelayMs);
}

function drain(): void {
  drainTimer = null;
  let next: AnnouncerState = state;
  let mutated = false;
  let latestThisTick: LatestAnnouncement | null = null;

  // Process assertive first so urgent messages aren't blocked behind polite ones.
  for (const politeness of ['assertive', 'polite'] as const) {
    const msg = queues[politeness].shift();
    if (msg === undefined) continue;

    const slot = nextSlot[politeness];
    nextSlot[politeness] = slot === 'a' ? 'b' : 'a';

    next = {
      ...next,
      [politeness]:
        slot === 'a'
          ? { slotA: msg.text, slotB: '' }
          : { slotA: '', slotB: msg.text },
    };
    mutated = true;

    // Both politenesses can drain in one tick, so "last" needs a rule: the
    // FIRST one set this tick wins, and the loop visits assertive first. That
    // is deliberate — assertive is what the screen reader interrupts with, so
    // it is what the student actually heard, and therefore what the panel must
    // be showing.
    latestThisTick ??= { text: msg.text, politeness, severity: msg.severity };
  }

  if (latestThisTick) next = { ...next, latest: latestThisTick };

  if (mutated) {
    state = next;
    emit();
  }

  if (queues.polite.length > 0 || queues.assertive.length > 0) {
    scheduleDrain();
  }
}

export function announce(message: string, options: AnnounceOptions = {}): void {
  const trimmed = typeof message === 'string' ? message.trim() : '';
  if (!trimmed) return;
  const politeness: Politeness = options.politeness ?? 'polite';
  queues[politeness].push({ text: trimmed, severity: options.severity ?? 'status' });
  scheduleDrain();
}

export function clear(politeness?: Politeness): void {
  if (politeness) {
    queues[politeness].length = 0;
    state = {
      ...state,
      [politeness]: EMPTY_REGION,
      // The panel is a copy of what the root last said, so clearing the region
      // that said it must clear the copy too — otherwise the panel keeps
      // showing a message the root no longer holds. Clearing the *other*
      // region leaves it alone.
      latest: state.latest?.politeness === politeness ? null : state.latest,
    };
  } else {
    queues.polite.length = 0;
    queues.assertive.length = 0;
    state = EMPTY_STATE;
  }
  emit();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSnapshot(): AnnouncerState {
  return state;
}

// Test-only helpers. Exported with leading underscore to signal they are not
// part of the public surface. Kept tiny so tests don't need to fight timers.
export function _setDrainDelay(ms: number): void {
  drainDelayMs = ms;
}

export function _flushSync(): void {
  if (drainTimer !== null) {
    clearTimeout(drainTimer);
    drainTimer = null;
  }
  while (queues.polite.length > 0 || queues.assertive.length > 0) {
    drain();
  }
}

export function _reset(): void {
  if (drainTimer !== null) {
    clearTimeout(drainTimer);
    drainTimer = null;
  }
  queues.polite.length = 0;
  queues.assertive.length = 0;
  nextSlot.polite = 'a';
  nextSlot.assertive = 'a';
  state = EMPTY_STATE;
  drainDelayMs = DEFAULT_DRAIN_DELAY_MS;
  emit();
}
